import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';

import styles from './ErrorBoundary.module.css';

interface Props {
    children: ReactNode;
    /** Shown instead of the default screen; used for smaller, local boundaries. */
    fallback?: ReactNode;
}

interface State {
    error: Error | null;
}

/**
 * Catches render-time crashes so a single broken component cannot take the
 * whole application down with a blank page.
 *
 * The user-facing copy is Dutch and free of technical detail; the stack goes to
 * the console for a developer to pick up.
 */
export class ErrorBoundary extends Component<Props, State> {
    override state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        // The one place a stack trace is genuinely useful to a developer.
        console.error('[ThuisCoach] Onverwachte fout in de interface', error, info.componentStack);
    }

    override render(): ReactNode {
        const { error } = this.state;

        if (error === null) {
            return this.props.children;
        }

        if (this.props.fallback !== undefined) {
            return this.props.fallback;
        }

        return (
            <div className={styles.screen} role="alert">
                <div className={styles.panel}>
                    <h1 className={styles.title}>Er ging iets mis</h1>

                    <Alert tone="danger">
                        Deze pagina kon niet worden geladen. Je gegevens zijn niet verloren gegaan.
                    </Alert>

                    <p className={styles.help}>
                        Probeer de pagina opnieuw te laden. Blijft het misgaan, ga dan terug naar
                        het overzicht.
                    </p>

                    <div className={styles.actions}>
                        <Button
                            onClick={() => {
                                window.location.reload();
                            }}
                        >
                            Pagina opnieuw laden
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                window.location.assign('/');
                            }}
                        >
                            Naar het overzicht
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}
